import { t } from "@lingui/macro";
import { FlashList, ListRenderItemInfo } from "@shopify/flash-list";
import graphql from "babel-plugin-relay/macro";
import React, {
  ComponentType,
  JSXElementConstructor,
  ReactElement,
  useMemo,
  useCallback,
} from "react";
import { Platform } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { Patron } from "../../types";
import { PatronsListItem } from "../PatronsListItem";
import { retainSelectedPatrons } from "../retainSelectedPatrons";
import { CustomerListQuery } from "./__generated__/CustomerListQuery.graphql";

type BoxProps = React.ComponentProps<typeof Box>;

type iCustomerListProps = {
  onCheckPlayer: (player: Patron, checked: boolean) => void;
  onRemovePlayer?: (playerId: string) => void;
  onAddUnknownCustomerClick?: () => void;
  checkNewDisabled?: boolean;
  ListHeaderComponent?:
    | ComponentType<any>
    | ReactElement<any, string | JSXElementConstructor<any>>
    | null;
  bg: BoxProps["bg"];
  players: string[];
  selectedPatrons?: Patron[];
  disabledPlayerIds?: string[];
  currentPatron?: Patron;
  eventIdToExcludeParticipantsFromResults?: string;
  customerQueryRef: PreloadedQuery<CustomerListQuery>;
  unknownCustomerEnabled?: boolean;
  keepItemInListAfterCheck?: boolean;
  selectionAction?: "swap" | "replace";
};

const CustomerListUnMemoized = ({
  onCheckPlayer,
  onRemovePlayer,
  checkNewDisabled,
  ListHeaderComponent,
  bg,
  players,
  selectedPatrons = [],
  disabledPlayerIds = [],
  currentPatron,
  eventIdToExcludeParticipantsFromResults,
  customerQueryRef,
  keepItemInListAfterCheck = false,
  selectionAction,
}: iCustomerListProps) => {
  const queryData = usePreloadedQuery<CustomerListQuery>(
    CustomerListQueryString,
    customerQueryRef
  );

  const customerData =
    queryData.session?.currentClientProfile?.member?.club?.clients;

  const shouldRemovePlayers = !keepItemInListAfterCheck;

  const allPlayers = useMemo(() => {
    const isCurrentUserInBooking = players.some(
      (player) => currentPatron?.playerId === player
    );

    const formattedMemberData: Patron[] =
      customerData?.edges
        ?.filter((p) =>
          shouldRemovePlayers ? !players.includes(p?.node?.id ?? "") : true
        )
        .map?.((customer) => ({
          playerId: customer?.node?.id || "",
          firstName: customer?.node?.firstName || "",
          membershipTypeId: customer?.node?.membershipType?.id,
          lastName: customer?.node?.lastName || "",
          profileImageId: customer?.node?.profileImage?.publicId || "",
          transportationType:
            customer?.node?.preferredTransportationType ?? undefined,
          isNotEligible:
            !!eventIdToExcludeParticipantsFromResults &&
            !customer?.node?.eligibleForEvent,
          type: "Customer",
          memberGroups:
            customer?.node?.memberGroups?.flatMap((g) =>
              g?.id ? [g.id] : []
            ) ?? [],
          email: customer?.node?.email ?? undefined,
          phoneNumber: customer?.node?.phoneNumber?.nationalNumber ?? undefined,
        })) || [];
    // Need to add in the current user in case they have been removed.
    const resultPlayers: Patron[] =
      !isCurrentUserInBooking && currentPatron?.type
        ? [
            {
              ...currentPatron,
              lastName: `${currentPatron?.lastName} (You)`,
            },
            ...formattedMemberData,
          ]
        : [...formattedMemberData];

    return retainSelectedPatrons({
      availablePatrons: resultPlayers,
      selectedPatrons,
      selectedPlayerIds: players,
      shouldRetainSelectedPatrons: keepItemInListAfterCheck,
    });
  }, [
    currentPatron,
    eventIdToExcludeParticipantsFromResults,
    customerData?.edges,
    players,
    selectedPatrons,
    keepItemInListAfterCheck,
    shouldRemovePlayers,
  ]);

  const isItemSelectedAndKept = useCallback(
    (id?: string | null) =>
      keepItemInListAfterCheck && (players?.includes(id ?? "") ?? false),
    [keepItemInListAfterCheck, players]
  );

  return (
    <Box bg={bg} flex={1}>
      <FlashList
        showsVerticalScrollIndicator={Platform.OS === "web"}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        data={allPlayers}
        keyExtractor={(item) => `customer-item-${item.playerId}`}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }: ListRenderItemInfo<Patron>) => {
          return (
            <PatronsListItem
              key={item.playerId}
              item={item}
              checkNewDisabled={
                disabledPlayerIds.includes(item.playerId ?? "") ||
                (isItemSelectedAndKept(item.playerId)
                  ? false
                  : checkNewDisabled)
              }
              initiallyChecked={
                isItemSelectedAndKept(item.playerId) ? true : false
              }
              handleCheck={onCheckPlayer}
              onRemovePlayer={onRemovePlayer}
              keepItemInListAfterCheck={keepItemInListAfterCheck}
              selectionAction={selectionAction}
            />
          );
        }}
        ListFooterComponent={<Box style={{ marginBottom: 128 }} />}
        ListEmptyComponent={
          <VStack
            width="100%"
            height="60%"
            alignItems="center"
            justifyContent="center"
          >
            <Text color="text-secondary" variant="mainBaseMedium">
              {t`No players match search`}
            </Text>
          </VStack>
        }
      />
    </Box>
  );
};

export const CustomerList = React.memo(CustomerListUnMemoized);

export const CustomerListQueryString = graphql`
  query CustomerListQuery(
    $search: String
    $players: [ID]
    $agendaId: ID
    $eventIdToExcludeParticipantsFromResults: ID
    $slotId: ID!
    $includeTransportationType: Boolean!
  ) {
    session {
      currentClientProfile {
        member {
          club {
            clients(
              first: 12
              search: $search
              players: $players
              agendaId: $agendaId
              active: true
              notRegisteredForEventId: $eventIdToExcludeParticipantsFromResults
            ) {
              edges {
                node {
                  ... on Customer {
                    id
                    firstName
                    lastName
                    email
                    phoneNumber {
                      nationalNumber
                    }
                    eligibleForEvent(
                      eventId: $eventIdToExcludeParticipantsFromResults
                    )
                    memberGroups {
                      id
                    }
                    membershipType {
                      id
                    }
                    preferredTransportationType(slotId: $slotId)
                      @include(if: $includeTransportationType) {
                      id
                      code
                      customCode
                    }
                    profileImage {
                      # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                      publicId: renderingKey
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
